import React, { HTMLAttributes, AnchorHTMLAttributes, ReactNode, MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import { CTASettings } from '@/utils/types/CTASettings';
import { lmLength, lmTrimChar, lmURLType, lmVideoEmbedURL } from '@/utils/helpers';
import { LMModal } from '@/utils/display';
import '@/styles/components/buttons.scss';
import '@/styles/components/modals.scss';
import { TagType } from '@/utils/types/TagType';
import { CustomAttributes } from '@/utils/types/CustomAttributes';

const CTA: React.FC<CTASettings> = ( { 
    buttonStyle = '', 
    children, 
    className = '',
    classes = [], 
    customAttributes = {},
    id = '', 
    iframeName,
    modal = false, 
    modalClass = '', 
    onClick, 
    target, 
    text = '', 
    uk = false, 
    url, 
} ) => {
    if ( typeof url === 'object' && url.url ) {
        url = url.url.replace( window.homeURL, '' ).replace( window.relativeHomeURL, '' );
    } else if ( typeof url === 'string' ) {
        url = lmLength( url ) && url.indexOf( window.homeURL ) === 0 ? url.replace( window.homeURL, '' ).replace( window.relativeHomeURL, '' ) : url;
    } else {
        url = '';
    }
    
    const html: ReactNode = children ? children : <span>{ text }</span>;
    const relativeURL: boolean = Boolean( url.length ) && url.indexOf( 'http' ) === -1 && url.indexOf( 'mailto:' ) === -1;
    const externalURL: boolean = Boolean( url.length ) && ! relativeURL && url.indexOf( window.homeURL ) !== 0;
    const vimeoVideo: boolean = url.indexOf( 'vimeo' ) > -1;
    const containsAnchor: boolean = Boolean( url.length ) && url.indexOf( '#' ) > -1;
    const anchorOnlyURL: boolean = containsAnchor && url.indexOf( '#' ) === 0;
    const relativeContainsFileExtension: boolean = relativeURL && url.indexOf( '.' ) > -1;
    const containsQueryParam: boolean = Boolean( url.length ) && url.indexOf( '?' ) > -1;
    const queryParamOnly: boolean = containsQueryParam && url.indexOf( '?' ) === 0;
    let href: string = relativeURL && ! anchorOnlyURL && ! queryParamOnly ? window.relativeHomeURL + '/' + lmTrimChar( url.replace( window.relativeHomeURL, '' ), '/' ) + ( containsAnchor || containsQueryParam || relativeContainsFileExtension ? '' : '/' ) : url;
    href = relativeURL ? href.replace( '//', '/' ) : href;
    href = vimeoVideo ? lmVideoEmbedURL( href ) : href;
    href = href.indexOf( window.homeURL ) > -1 ? lmTrimChar( href, '/' ) + '/' : href;
    const urlType: string = lmURLType( href );
    const urlTarget = target ? target : ( relativeURL || href.indexOf( window.homeURL ) === 0 ? '_self' : '_blank' );
    const ukToggle = modal && anchorOnlyURL;
    const ukScroll = ! modal && anchorOnlyURL && ! uk;
    const ariaHidden = text.indexOf( 'lm-video' ) > -1 && text.indexOf( 'embed' ) > -1;
    
    let Tag: TagType = lmLength( href ) || onClick ? Link : 'span';

    let attributes: HTMLAttributes<HTMLAnchorElement> | AnchorHTMLAttributes<HTMLAnchorElement> = {
	    title: text,
        target: urlTarget,
        'data-url-type': urlType,
    }
    
    classes.push( 'lm-cta' );

    if ( className.length ) {
        classes.push( className );
    }
    if ( buttonStyle ) {
        classes.push( 'button', buttonStyle );
    }
    if ( modal && ! anchorOnlyURL && ( vimeoVideo || ! externalURL ) ) {
        classes.push( 'lm-modal' );
        attributes.onClick = ( e ) => { new LMModal( e as any ) };
    }
    if ( anchorOnlyURL || queryParamOnly || onClick ) {
        if ( ! onClick ) {
            attributes.href = href;
        }
        Tag = 'a';
    } else if ( Tag === Link ) {
        attributes.to = href;
    }
    if ( id ) {
        attributes.id = id;
    }
    if ( onClick ) {
        attributes.onClick = onClick;
    }
    if ( ariaHidden ) {
        attributes['aria-hidden'] = true;
    }
    if ( urlType === 'pdf' ) {
        attributes.rel = 'nofollow';
    }
    if ( customAttributes ) {
        attributes = { ...attributes, ...customAttributes as CustomAttributes };
    }
    if ( modal && modalClass ) {
        attributes['data-modal-class'] = modalClass;
    }

    const classString: string = classes.join( ' ' );
    attributes.className = classString;

    function HTML() {
        return <>{ html }</>;
    }
    return ( 
        <>
        { href || text || onClick ? 
            <Tag { ...attributes }>
                <HTML />
            </Tag>
        : <HTML />
        }
        </>
    )
}
export default CTA;
